import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Web Push (PWA notifications) hook.
 *
 * Supports modern browsers + iOS Safari 16.4+ (standalone PWA only).
 *
 * Diagnostic logs prefixed `[push]` are intentional and gated by the
 * URL flag `?debugPush=1` or `localStorage.sdrive_debug_push=1` so
 * production noise stays at zero unless the operator opts in.
 */

const DEBUG = (() => {
  try {
    if (typeof window === "undefined") return false;
    if (window.location && window.location.search.includes("debugPush=1")) return true;
    return window.localStorage && window.localStorage.getItem("sdrive_debug_push") === "1";
  } catch { return false; }
})();
const dlog  = (...a) => { if (DEBUG) try { console.log("[push]", ...a); } catch {} };
const dwarn = (...a) => { if (DEBUG) try { console.warn("[push]", ...a); } catch {} };

// base64url → Uint8Array (needed by PushManager.subscribe). We pass the
// underlying ArrayBuffer to subscribe() to maximise iOS compatibility.
function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

// ─── Environment introspection ────────────────────────────────────────
// Computed each call (NOT cached at module load) so React renders pick
// up the real state once the SW + window globals are bootstrapped on
// iOS Safari standalone PWAs.
function detectEnv() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { hasSW: false, hasPM: false, hasNotif: false, isStandalone: false, isIOS: false };
  }
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  // iOS standalone PWA → window.navigator.standalone === true
  // Other PWAs (Android Chrome / desktop) → display-mode: standalone media query
  const isStandalone = (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches)
                       || (window.navigator.standalone === true);
  return {
    hasSW: "serviceWorker" in navigator,
    hasPM: "PushManager" in window,
    hasNotif: "Notification" in window,
    isStandalone,
    isIOS,
  };
}

export function useWebPush() {
  const [env, setEnv] = useState(detectEnv);
  const [permission, setPermission] = useState(() => {
    try { return ("Notification" in window) ? Notification.permission : "denied"; }
    catch { return "denied"; }
  });
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  // Last enable() failure surfaced for the UI (e.g. "ios-needs-install").
  const [lastError, setLastError] = useState(null);

  // Re-probe the environment a few times after mount — Safari sometimes
  // exposes Push capabilities only once the SW window globals are ready.
  useEffect(() => {
    let cancelled = false;
    const tick = (delay) => setTimeout(() => {
      if (cancelled) return;
      setEnv(detectEnv());
    }, delay);
    const ts = [tick(0), tick(500), tick(2000)];
    return () => { cancelled = true; ts.forEach(clearTimeout); };
  }, []);

  // ─── Support evaluation ────────────────────────────────────────────
  // We treat the API as "supported" when the 3 web standards exist AND
  // (on iOS only) the page is in standalone PWA mode — Apple disables
  // push outside that context. Non-iOS browsers (Android Chrome,
  // desktop Edge/Chrome/Firefox) work in any tab.
  const supported = env.hasSW && env.hasPM && env.hasNotif && (!env.isIOS || env.isStandalone);
  // Helper for the UI to nudge iOS users towards the PWA install flow.
  const iosNeedsInstall = env.isIOS && !env.isStandalone && env.hasNotif;

  // ─── Initial subscription state probe ──────────────────────────────
  const refresh = useCallback(async () => {
    if (!env.hasSW || !env.hasPM) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setSubscribed(!!sub);
      dlog("refresh: subscription present =", !!sub);
    } catch (e) {
      dwarn("refresh failed:", e);
      setSubscribed(false);
    }
  }, [env.hasSW, env.hasPM]);

  useEffect(() => { refresh(); }, [refresh]);

  // Keep `permission` reactive — Safari fires `permissionchange` on the
  // permission status object (when wrapped via Permissions API) but
  // mirrors the value into Notification.permission too.
  useEffect(() => {
    if (!env.hasNotif) return;
    let unsub;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: "notifications" });
          const onChange = () => setPermission(Notification.permission);
          status.addEventListener?.("change", onChange);
          unsub = () => status.removeEventListener?.("change", onChange);
        }
      } catch { /* not all browsers expose Permissions API for notifications */ }
    })();
    return () => { try { unsub?.(); } catch {} };
  }, [env.hasNotif]);

  // ─── Enable: register SW, request permission, subscribe ────────────
  const enable = useCallback(async () => {
    setLastError(null);
    // Re-probe right before doing anything — the user may have just
    // installed the PWA and reopened it.
    const cur = detectEnv();
    setEnv(cur);
    dlog("enable() env =", cur, "permission =", Notification.permission);

    if (!cur.hasSW || !cur.hasPM || !cur.hasNotif) {
      const why = !cur.hasSW ? "no-serviceworker"
                : !cur.hasPM ? "no-pushmanager"
                : "no-notification-api";
      setLastError(why);
      return { ok: false, reason: why };
    }
    if (cur.isIOS && !cur.isStandalone) {
      setLastError("ios-needs-install");
      return { ok: false, reason: "ios-needs-install" };
    }

    setBusy(true);
    try {
      // 1) Register service worker AND wait for it to be active. On
      //    iOS the freshly-registered SW isn't immediately active and
      //    pushManager.subscribe() will throw if we skip this wait.
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      dlog("SW ready, scope =", reg.scope);

      // 2) Ask permission (only if still default — re-asking after a
      //    grant is fine on most browsers but redundant).
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      dlog("permission =", perm);
      if (perm !== "granted") { setLastError("denied"); setBusy(false); return { ok: false, reason: "denied" }; }

      // 3) Fetch VAPID public key
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.available || !data.public_key) {
        setLastError("no-vapid");
        setBusy(false);
        return { ok: false, reason: "no-vapid" };
      }

      // 4) Subscribe (re-create the subscription idempotently — we
      //    drop any stale one first so a server-side rotated VAPID key
      //    doesn't strand the device on an unverifiable subscription).
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch {}
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // ArrayBuffer maximises iOS compatibility — Uint8Array works on
        // most browsers but Safari 16.4 has been picky historically.
        applicationServerKey: urlB64ToUint8Array(data.public_key).buffer,
      });
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        user_agent: navigator.userAgent.slice(0, 250),
      });
      setSubscribed(true);
      dlog("subscribe ok, endpoint =", raw.endpoint?.slice(0, 60) + "…");
      setBusy(false);
      return { ok: true };
    } catch (e) {
      const msg = e?.message || "error";
      dwarn("enable() failed:", e);
      setLastError(msg);
      setBusy(false);
      return { ok: false, reason: msg };
    }
  }, []);

  const disable = useCallback(async () => {
    if (!env.hasSW || !env.hasPM) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const raw = sub.toJSON();
        try { await api.delete("/push/subscribe", { data: { endpoint: raw.endpoint, keys: raw.keys } }); } catch {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      dlog("disable() ok");
    } finally { setBusy(false); }
  }, [env.hasSW, env.hasPM]);

  return {
    supported,
    iosNeedsInstall,
    isIOS: env.isIOS,
    isStandalone: env.isStandalone,
    permission,
    subscribed,
    busy,
    lastError,
    enable,
    disable,
    refresh,
  };
}
